import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Loader2, ArrowLeft, MessageSquare, Send, Filter,
  AlertOctagon, Clock, CheckCircle2
} from 'lucide-react';

const STATUS_META = {
  open:        { label: 'Open',        color: 'bg-amber-100 text-amber-800' },
  in_progress: { label: 'In progress', color: 'bg-sky-100 text-sky-800' },
  resolved:    { label: 'Resolved',    color: 'bg-emerald-100 text-emerald-800' },
  closed:      { label: 'Closed',      color: 'bg-slate-100 text-slate-600' },
};
const PRIORITY_META = {
  high:   { color: 'bg-red-100 text-red-700 border-red-200' },
  medium: { color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
  low:    { color: 'bg-slate-100 text-slate-600 border-slate-200' },
};

const AdminSupport = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [openTicket, setOpenTicket] = useState(null);
  const [reply, setReply] = useState('');
  const [replying, setReplying] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/support/tickets');
      setTickets(res.data.tickets || res.data || []);
    } catch (err) {
      toast.error('Failed to load support tickets');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const filtered = tickets.filter((t) => statusFilter === 'all' || t.status === statusFilter);
  const counts = {
    all:         tickets.length,
    open:        tickets.filter((t) => t.status === 'open').length,
    in_progress: tickets.filter((t) => t.status === 'in_progress').length,
    resolved:    tickets.filter((t) => t.status === 'resolved').length,
  };

  const updateStatus = async (ticket, newStatus) => {
    try {
      await api.put(`/admin/support/tickets/${ticket.id}`, { status: newStatus });
      toast.success(`Marked ${STATUS_META[newStatus]?.label || newStatus}`);
      load();
    } catch { toast.error('Failed to update status'); }
  };

  const sendReply = async () => {
    if (!openTicket || !reply.trim()) return;
    setReplying(true);
    try {
      await api.put(`/admin/support/tickets/${openTicket.id}`, {
        add_response: { message: reply.trim(), is_staff: true },
      });
      toast.success('Reply sent');
      setReply('');
      load();
      // Refresh the open ticket
      const fresh = await api.get(`/admin/support/tickets/${openTicket.id}`);
      setOpenTicket(fresh.data.ticket || fresh.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send reply');
    } finally {
      setReplying(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div>
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="support-back">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <h1 className="font-semibold text-slate-800 mt-1 flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-emerald-500" /> Support Inbox
            </h1>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/intern/dashboard')} data-testid="support-intern-back">
            My tasks
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-4 md:p-6">
        {/* Filter tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          {[
            { key: 'all',         label: 'All tickets',   color: 'text-slate-600' },
            { key: 'open',        label: 'Open',          color: 'text-amber-600' },
            { key: 'in_progress', label: 'In progress',   color: 'text-sky-600' },
            { key: 'resolved',    label: 'Resolved',      color: 'text-emerald-600' },
          ].map((s) => {
            const active = statusFilter === s.key;
            return (
              <button
                key={s.key}
                onClick={() => setStatusFilter(s.key)}
                className={`text-left rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-emerald-400 ${
                  active ? 'ring-2 ring-emerald-500 shadow-md scale-[1.02]' : 'hover:shadow-md hover:scale-[1.01]'
                }`}
                data-testid={`support-tile-${s.key}`}
              >
                <Card className="border-0 shadow-sm">
                  <CardContent className="p-4">
                    <p className="text-xs uppercase tracking-wider text-slate-400">{s.label}</p>
                    <p className={`text-2xl font-bold mt-1 ${s.color}`}>{counts[s.key] || 0}</p>
                  </CardContent>
                </Card>
              </button>
            );
          })}
        </div>

        {/* Tickets list */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-sm font-semibold text-slate-500 flex items-center gap-2">
              <Filter className="w-4 h-4" /> {statusFilter === 'all' ? 'All tickets' : STATUS_META[statusFilter]?.label} · {filtered.length}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="py-10 text-center"><Loader2 className="w-6 h-6 mx-auto animate-spin text-slate-400" /></div>
            ) : filtered.length === 0 ? (
              <div className="py-10 text-center text-slate-400">
                <MessageSquare className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                <p className="text-sm">No tickets {statusFilter !== 'all' ? `in ${STATUS_META[statusFilter]?.label}` : ''} — nice, inbox zero.</p>
              </div>
            ) : (
              <div className="divide-y">
                {filtered.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => { setOpenTicket(t); setReply(''); }}
                    className="w-full text-left p-4 hover:bg-slate-50 flex items-center gap-3"
                    data-testid={`support-ticket-${t.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-slate-800 truncate">{t.subject}</p>
                        <Badge className={`${STATUS_META[t.status]?.color || 'bg-slate-100 text-slate-600'} text-[10px]`}>
                          {STATUS_META[t.status]?.label || t.status}
                        </Badge>
                        <Badge variant="outline" className={`${PRIORITY_META[t.priority]?.color || ''} text-[10px]`}>
                          {t.priority || 'medium'}
                        </Badge>
                      </div>
                      <p className="text-xs text-slate-500 mt-1 truncate">{t.description}</p>
                      <p className="text-[11px] text-slate-400 mt-1">
                        {(t.created_at || '').split('T')[0]} · {(t.responses || []).length} replies
                      </p>
                    </div>
                    <ArrowLeft className="w-4 h-4 text-slate-300 rotate-180" />
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Ticket detail dialog */}
      <Dialog open={!!openTicket} onOpenChange={(o) => !o && setOpenTicket(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="support-ticket-dialog">
          {openTicket && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">{openTicket.subject}</DialogTitle>
                <DialogDescription>
                  <span className="text-xs">
                    From <span className="font-semibold">{openTicket.user_email || openTicket.user_id}</span> · {openTicket.category} · {(openTicket.created_at || '').split('T')[0]}
                  </span>
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={STATUS_META[openTicket.status]?.color || 'bg-slate-100'}>{STATUS_META[openTicket.status]?.label || openTicket.status}</Badge>
                  <Badge variant="outline" className={PRIORITY_META[openTicket.priority]?.color}>
                    <AlertOctagon className="w-3 h-3 mr-1" /> {openTicket.priority || 'medium'}
                  </Badge>
                  <div className="flex-1" />
                  <Select value={openTicket.status} onValueChange={(v) => updateStatus(openTicket, v)}>
                    <SelectTrigger className="w-40" data-testid="support-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(STATUS_META).map(([k, m]) => (
                        <SelectItem key={k} value={k}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="p-3 rounded-md bg-slate-50 border">
                  <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Original message</p>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{openTicket.description}</p>
                </div>

                <div>
                  <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Thread</p>
                  <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                    {(openTicket.responses || []).length === 0 && (
                      <p className="text-sm text-slate-400 italic text-center py-4">No replies yet. Be the first — parents wait long enough.</p>
                    )}
                    {(openTicket.responses || []).map((r) => (
                      <div key={r.id} className={`p-2 rounded-md text-sm ${r.is_staff ? 'bg-emerald-50 border border-emerald-100' : 'bg-white border'}`}>
                        <div className="flex items-center gap-2 text-[11px] text-slate-500 mb-1">
                          <span className="font-semibold">{r.is_staff ? 'Zazu Team' : 'Parent'}</span>
                          <Clock className="w-3 h-3" /> {(r.created_at || '').replace('T', ' ').slice(0, 16)}
                        </div>
                        <p className="whitespace-pre-wrap text-slate-700">{r.message}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Reply</p>
                  <Textarea
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    rows={3}
                    placeholder="Write a kind, clear reply. Escalate anything auth/billing/refund to a mentor before sending."
                    data-testid="support-reply-input"
                  />
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setOpenTicket(null)}>Close</Button>
                <Button
                  onClick={sendReply}
                  disabled={replying || !reply.trim()}
                  className="bg-emerald-500 hover:bg-emerald-600"
                  data-testid="support-send-reply"
                >
                  {replying ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                  Send reply
                </Button>
                {openTicket.status !== 'resolved' && (
                  <Button
                    variant="outline"
                    onClick={() => { updateStatus(openTicket, 'resolved'); setOpenTicket(null); }}
                    className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    data-testid="support-mark-resolved"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-1" /> Mark resolved
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSupport;
